/**
 * Recovering from a chunk that no longer exists.
 *
 * The app code-splits heavily (`dynamic()` for the settings panel, the chapter
 * matrix, the copilot and more), so a page keeps fetching JavaScript long after
 * it first loaded. Each of those files has a content hash in its name.
 *
 * Deploy while someone has a tab open and those two facts collide: their page
 * is running build A, holding a webpack runtime that knows chunk
 * `826-87bb5f43…`, and the server is now serving build B where that file does
 * not exist. The moment they open something lazily loaded, the fetch 404s and
 * throws
 *
 *     ChunkLoadError: Loading chunk 826 failed
 *
 * which is not a bug in the feature they clicked. It means "your tab is older
 * than the server". The only fix is to fetch the new page, and the writer
 * cannot be expected to know that — from where they sit, a button stopped
 * working.
 *
 * So: reload once, automatically. Once is the important part. If the reload
 * does not fix it (a genuinely broken deploy, a proxy serving 404s) a second
 * one produces an infinite refresh loop, which is far worse than an error
 * message. The one-shot flag lives in sessionStorage so it clears with the tab
 * rather than following someone around forever.
 */

export const RELOAD_FLAG = "nc_chunk_reloaded";

/**
 * Is this the "your tab is stale" error?
 *
 * Matched on both the name and the message because bundlers and browsers
 * disagree about which one carries the information, and a missed match here
 * costs a writer their unsaved click.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as { name?: unknown }).name;
  if (typeof name === "string" && name === "ChunkLoadError") return true;

  const message =
    typeof error === "string"
      ? error
      : typeof (error as { message?: unknown }).message === "string"
        ? ((error as { message: string }).message)
        : "";

  return (
    /ChunkLoadError/i.test(message) ||
    /Loading chunk \S+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    // Safari and Firefox wording for the same underlying fetch failure of a
    // module that has since been replaced.
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

/** Have we already tried reloading this tab for this reason? */
export function alreadyReloaded(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === "1";
  } catch {
    // No sessionStorage means no loop protection, so decline to reload at all.
    // An error message beats a refresh loop.
    return true;
  }
}

export function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch {
    /* best-effort */
  }
}

/**
 * Clear the one-shot flag once the tab is demonstrably healthy, so a writer who
 * keeps a tab open across two deploys is rescued both times rather than only
 * the first.
 */
export function clearReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* best-effort */
  }
}

/** The decision, as one testable expression. */
export function shouldReloadFor(error: unknown, hasReloaded: boolean): boolean {
  return isChunkLoadError(error) && !hasReloaded;
}

/**
 * Reload if this error means the tab is out of date. Returns whether it acted,
 * so a caller can fall through to showing an error page when it did not.
 */
export function recoverFromChunkError(
  error: unknown,
  reload: () => void = () => window.location.reload()
): boolean {
  if (!shouldReloadFor(error, alreadyReloaded())) return false;
  markReloaded();
  reload();
  return true;
}
